import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search, Edit, Trash2, Key, Mail, User, Shield, Check, X, Eye, EyeOff, AlertTriangle, UserPlus } from 'lucide-react';
import EnhancedSecurityWrapper from '@/components/admin/EnhancedSecurityWrapper';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useSecureAdminActions } from '@/hooks/useSecureAdminActions';
import { useEnhancedAuth } from '@/hooks/useEnhancedAuth';
import { toast } from 'sonner';

const AdminUsers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [newEmail, setNewEmail] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isCreatingCurrentUser, setIsCreatingCurrentUser] = useState(false);
  const [passwordReset, setPasswordReset] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [newUserForm, setNewUserForm] = useState({
    email: '',
    username: '',
    role: 'admin'
  });
  
  const { user: currentUser } = useAuth();
  const { adminUser, isSuperAdmin } = useEnhancedAuth({ requireAdmin: false });
  const {
    updateAdminUserMutation,
    requestPasswordResetMutation,
    createAdminUserMutation,
    deleteAdminUserMutation,
    canManageUsers
  } = useSecureAdminActions();

  const { data: adminUsers = [], isLoading, error, refetch } = useQuery({
    queryKey: ['admin-users', searchTerm],
    queryFn: async () => {
      console.log('Fetching admin users for current user:', currentUser?.id);
      
      let query = supabase
        .from('admin_users')
        .select('*')
        .order('created_at', { ascending: false });

      if (searchTerm) {
        query = query.or(`email.ilike.%${searchTerm}%,username.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query;
      console.log('Admin users query result:', data, 'error:', error);
      
      if (error) {
        console.error('Admin users fetch error:', error);
        throw error;
      }
      
      return data || [];
    },
  });

  const createCurrentUserAsAdmin = async () => {
    if (!currentUser) return;
    
    setIsCreatingCurrentUser(true);
    try {
      console.log('Creating admin user for:', currentUser.email);
      
      const { error } = await supabase
        .from('admin_users')
        .insert({
          user_id: currentUser.id,
          email: currentUser.email || 'admin@example.com',
          username: currentUser.email?.split('@')[0] || 'admin',
          role: 'super_admin',
          is_active: true
        });

      if (error) {
        console.error('Error creating admin user:', error);
        toast.error('Failed to create admin user: ' + error.message);
      } else {
        toast.success('Admin user created successfully! Refreshing...');
        await refetch();
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (err) {
      console.error('Error creating admin user:', err);
      toast.error('Failed to create admin user');
    } finally {
      setIsCreatingCurrentUser(false);
    }
  };

  const handleUpdateUser = (updates: any) => {
    if (editingUser) {
      updateAdminUserMutation.mutate({ id: editingUser.id, updates });
    }
  };

  const handleCreateUser = () => {
    createAdminUserMutation.mutate(newUserForm);
    setIsCreateDialogOpen(false);
    setNewUserForm({ email: '', username: '', role: 'admin' });
  };

  const handleDeleteUser = (userId: string) => {
    if (window.confirm('Are you sure you want to delete this admin user? This action cannot be undone.')) {
      deleteAdminUserMutation.mutate(userId);
    }
  };

  const changePasswordWithCurrent = async () => {
    if (!passwordReset.currentPassword || !passwordReset.newPassword || !passwordReset.confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (passwordReset.newPassword !== passwordReset.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordReset.newPassword.length < 8) {
      toast.error('New password must be at least 8 characters long');
      return;
    }

    if (editingUser?.user_id !== currentUser?.id) {
      toast.error('You can only change your own password directly. Use email reset for other users.');
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordReset.newPassword
      });

      if (error) throw error;

      toast.success('Password updated successfully');
      setPasswordReset({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

    } catch (error: any) {
      console.error('Password change error:', error);
      toast.error(error.message || 'Failed to update password');
    }
  };

  const resetForm = () => {
    setNewEmail('');
    setPasswordReset({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const isCurrentUser = (adminUser: any) => {
    return currentUser?.id === adminUser.user_id;
  };

  const canEditUser = (targetUser: any) => {
    if (isSuperAdmin) {
      return true;
    }
    return isCurrentUser(targetUser);
  };

  const canDeleteUser = (targetUser: any) => {
    return isSuperAdmin && !isCurrentUser(targetUser);
  };

  const validAdminUsers = adminUsers.filter(user => user != null);

  if ((validAdminUsers.length === 0 && !isLoading) || error) {
    return (
      <EnhancedSecurityWrapper requireAdmin={false}>
        <div className="space-y-6">
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <Card className="p-8 max-w-md mx-auto text-center">
              <AlertTriangle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Admin Setup Required</h2>
              <p className="text-gray-600 mb-6">
                {error 
                  ? `There was an issue accessing admin users: ${error.message}`
                  : 'No admin users found in the system. You need to set up your admin account first.'
                }
              </p>
              <p className="text-sm text-gray-500 mb-6">
                This will create a super admin account for: <strong>{currentUser?.email}</strong>
              </p>
              <Button 
                onClick={createCurrentUserAsAdmin}
                disabled={isCreatingCurrentUser}
                className="w-full"
              >
                {isCreatingCurrentUser ? 'Creating Admin Account...' : 'Set Up My Admin Account'}
              </Button>
            </Card>
          </div>
        </div>
      </EnhancedSecurityWrapper>
    );
  }

  return (
    <EnhancedSecurityWrapper requireAdmin={true}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">User Management</h1>
            <p className="text-gray-600 mt-1">Manage admin users and permissions</p>
          </div>
          {canManageUsers && (
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Admin User
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Admin User</DialogTitle>
                  <DialogDescription>
                    Create a new admin user. They will need to sign up with the specified email address.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="new-email">Email</Label>
                    <Input
                      id="new-email"
                      value={newUserForm.email}
                      onChange={(e) => setNewUserForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="Enter email address"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-username">Username</Label>
                    <Input
                      id="new-username"
                      value={newUserForm.username}
                      onChange={(e) => setNewUserForm(prev => ({ ...prev, username: e.target.value }))}
                      placeholder="Enter username"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-role">Role</Label>
                    <Select
                      value={newUserForm.role}
                      onValueChange={(value) => setNewUserForm(prev => ({ ...prev, role: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        {isSuperAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCreateUser}
                    disabled={!newUserForm.email || !newUserForm.username}
                  >
                    Create User
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Security Notice */}
        <Card className="p-4 bg-green-50 border-green-200">
          <div className="flex items-start space-x-3">
            <Shield className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-green-800">Admin Access Active</h3>
              <p className="text-sm text-green-700 mt-1">
                {isSuperAdmin 
                  ? 'You have super admin privileges. Use them responsibly. All actions are logged and monitored.'
                  : 'You have admin privileges. You can only edit your own profile. Role changes require super admin access.'
                }
              </p>
            </div>
          </div>
        </Card>

        {/* Search */}
        <Card className="p-4 sm:p-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Users Table */}
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead className="hidden sm:table-cell">Role</TableHead>
                  <TableHead className="hidden md:table-cell">Status</TableHead>
                  <TableHead className="hidden lg:table-cell">Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    </TableCell>
                  </TableRow>
                ) : (
                  validAdminUsers.map((adminUser) => (
                    <TableRow key={adminUser.id}>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">
                              {adminUser.email}
                              {isCurrentUser(adminUser) && (
                                <Badge variant="outline" className="ml-2 text-xs">You</Badge>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{adminUser.username || 'No username'}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <Badge variant={adminUser.role === 'super_admin' ? "destructive" : "secondary"}>
                          <Shield className="w-3 h-3 mr-1" />
                          {adminUser.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <Badge variant={adminUser.is_active ? "default" : "destructive"}>
                          {adminUser.is_active ? (
                            <>
                              <Check className="w-3 h-3 mr-1" />
                              Active
                            </>
                          ) : (
                            <>
                              <X className="w-3 h-3 mr-1" />
                              Inactive
                            </>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {formatDate(adminUser.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end space-x-2">
                          {canEditUser(adminUser) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingUser(adminUser);
                                setNewEmail(adminUser.email);
                                setIsEditDialogOpen(true);
                                resetForm();
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                          
                          {canDeleteUser(adminUser) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteUser(adminUser.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
          setIsEditDialogOpen(open);
          if (!open) {
            resetForm();
          }
        }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Admin User</DialogTitle>
              <DialogDescription>
                Update admin user information and manage access
              </DialogDescription>
            </DialogHeader>
            {editingUser && (
              <div className="space-y-6">
                {/* Email Update - Super admins only or own profile */}
                {(isSuperAdmin || isCurrentUser(editingUser)) && (
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="flex gap-2">
                      <Input
                        id="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder="Enter email"
                      />
                      <Button
                        size="sm"
                        onClick={() => handleUpdateUser({ email: newEmail })}
                        disabled={!newEmail || newEmail === editingUser?.email}
                      >
                        <Mail className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Role Update - Super admins only */}
                {isSuperAdmin && !isCurrentUser(editingUser) && (
                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Select
                      value={editingUser?.role}
                      onValueChange={(value) => handleUpdateUser({ role: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="super_admin">Super Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Status Update - Super admins only, not self */}
                {isSuperAdmin && !isCurrentUser(editingUser) && (
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={editingUser?.is_active ? "active" : "inactive"}
                      onValueChange={(value) => handleUpdateUser({ is_active: value === "active" })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Password Management */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Password Management</h4>
                  <div className="space-y-4">
                    
                    {/* Direct password change - Only for current user */}
                    {isCurrentUser(editingUser) && (
                      <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                        <h5 className="text-sm font-medium text-blue-900 mb-3">Change Your Password</h5>
                        <div className="space-y-3">
                          <div>
                            <Label htmlFor="currentPassword" className="text-sm">Current Password</Label>
                            <div className="relative">
                              <Input
                                id="currentPassword"
                                type={showCurrentPassword ? "text" : "password"}
                                value={passwordReset.currentPassword}
                                onChange={(e) => setPasswordReset(prev => ({ ...prev, currentPassword: e.target.value }))}
                                placeholder="Enter current password"
                                className="pr-10"
                              />
                              <button
                                type="button"
                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                          
                          <div>
                            <Label htmlFor="newPassword" className="text-sm">New Password</Label>
                            <div className="relative">
                              <Input
                                id="newPassword"
                                type={showNewPassword ? "text" : "password"}
                                value={passwordReset.newPassword}
                                onChange={(e) => setPasswordReset(prev => ({ ...prev, newPassword: e.target.value }))}
                                placeholder="Enter new password (min 8 characters)"
                                className="pr-10"
                              />
                              <button
                                type="button"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                          
                          <div>
                            <Label htmlFor="confirmPassword" className="text-sm">Confirm New Password</Label>
                            <Input
                              id="confirmPassword"
                              type="password"
                              value={passwordReset.confirmPassword}
                              onChange={(e) => setPasswordReset(prev => ({ ...prev, confirmPassword: e.target.value }))}
                              placeholder="Confirm new password"
                            />
                          </div>
                          
                          <Button
                            onClick={changePasswordWithCurrent}
                            className="w-full"
                            disabled={!passwordReset.currentPassword || !passwordReset.newPassword || !passwordReset.confirmPassword}
                          >
                            <Key className="w-4 h-4 mr-2" />
                            Change Password
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Email Reset Option */}
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-700 mb-3">
                        {isCurrentUser(editingUser) 
                          ? "Alternative: Send password reset email to your account."
                          : "Send password reset email to this user's account."
                        }
                      </p>
                      <Button
                        onClick={() => requestPasswordResetMutation.mutate({ 
                          email: editingUser?.email,
                          targetUserId: editingUser?.user_id 
                        })}
                        variant="outline"
                        size="sm"
                        disabled={requestPasswordResetMutation.isPending}
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Send Password Reset Email
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </EnhancedSecurityWrapper>
  );
};

export default AdminUsers;
